import { makeStyles } from "tss-react/mui";
import { Box, Tab, Tabs, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState, useRef } from "react";
import { LayersOutlined as LayersIcon, DirectionsCarOutlined as CarIcon, Menu as MenuIcon, Add as AddIcon } from "@mui/icons-material";
import ContainerButton, { OperationsMapButton, OperationsMapTextButton } from "./common/ContainerButton";
import StyleSelector from "./common/StyleSelector";
import NavigationButtonWidget from "./common/NavigationButtonWidget";
import { useDevice } from "../../../providers/DeviceProvider";
import { useOperationsMap } from "./OperationsMapProvider";
import { useStation } from "../../../providers/StationProvider";
import OperationsMap from "./map/OperationsMap";
import ActiveTasksPanel from "./common/ActiveTasksPanel";
import { useAuth } from "@emberly/zenith-client";
import LayerSettingsPanel from "./common/LayerSettingsPanel";
import TaskDialog from "./common/TaskDialog";
import PopupDialog from "./common/PopupDialog";
import VehiclesPanel from "./common/VehiclesPanel";
import VehicleDialog from "./common/VehicleDialog";

const useStyles = makeStyles()(
  (theme) => ({
    root: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%"
    },
    map: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%"
    },
    topLeftButton: {
      position: "absolute",
      top: theme.spacing(1.5),
      left: theme.spacing(1.5),
      zIndex: 3
    },
    topLeftButtonSlim: {
      position: "absolute",
      top: theme.spacing(1.5),
      right: `calc(${theme.spacing(3)} + 56px)`
    },
    topRightButton: {
      position: "absolute",
      top: theme.spacing(1.5),
      right: theme.spacing(1.5)
    },
    styleSelector: {
      position: "absolute",
      bottom: theme.spacing(1.5),
      left: theme.spacing(1.5)
    },
    styleSelectorTopLeftExpanded: {
      left: `calc(${theme.spacing(3)} + 390px)`
    },
    createMissionButton: {
      position: "absolute",
      top: theme.spacing(1.5),
      right: "80px",
      zIndex: 1
    },
    createMissionButtonTopRightExpanded: {
      right: `calc(${theme.spacing(3)} + 390px)`
    },
    createMissionButtonSlim: {
      position: "absolute",
      bottom: theme.spacing(1.5),
      right: theme.spacing(1.5),
      zIndex: 1,
      background: theme.palette.primary.main,
      "& svg": {
        color: theme.palette.primary.contrast

      }
    },
    navigationWidgetSlim: {
      position: "absolute",
      bottom: "108px",
      left: theme.spacing(1.5)
    },
    navigationWidget: {
      position: "absolute",
      bottom: theme.spacing(1.5),
      left: `calc(${theme.spacing(3)} + 88px)`
    },
    navigationWidgetTopLeftExpanded: {
      left: `calc(${theme.spacing(4.5)} + 88px + 390px)`
    }
  })
);

export default function OperationsMapLayout() {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { slim, setSidebarOpen } = useDevice();
  const { station, createMissionDraft } = useStation();
  const theme = useTheme();
  const { getAccessTokenSilently } = useAuth();
  const [popupDialog, setPopupDialog] = useState(null);


  const {
    topLeftExpanded,
    setTopLeftExpanded,
    topRightExpanded,
    setTopRightExpanded,
    styleSelectorExpanded,
    setStyleSelectorExpanded,
    setDispatchLayerHoverId,
    style,
    setStyle,
    containerRef,
    setOperationsMap,
    operationsMap,
    openTaskDialog,
    openVehicleDialog,
    setVehicleLayerHoverId,
    settings,
    setLoaded,
    mapTasksTabIndex,
    setMapTasksTabIndex
  } = useOperationsMap();

  const onChangeTabIndex = useCallback((_, idx) => {
    setMapTasksTabIndex(idx);
  }, [setMapTasksTabIndex]);

  const stationRef = useRef(station);
  stationRef.current = station;
  const hasStation = !!station;

  useEffect(() => {
    const container = containerRef.current;

    if (!!container && hasStation) {
      let m = new OperationsMap("main",
        {
          storage: "localStorage",
          settings,
          container,
          station: stationRef.current,
          theme,
          t,
          getAccessToken: getAccessTokenSilently
        }
      );

      setOperationsMap(m);

      m.on("dispatchLayerHover", setDispatchLayerHoverId);
      m.on("dispatchLayerClick", openTaskDialog);

      m.on("vehicleLayerHover", setVehicleLayerHoverId);
      m.on("vehicleLayerClick", openVehicleDialog);

      m.on("popupDialog", setPopupDialog);
      m.on("loaded", setLoaded);

      return () => {
        m?.destroy();
      }
    }
  }, [hasStation, theme, t, getAccessTokenSilently, settings]);

  const onStyleSelected = useCallback((value) => {
    setStyleSelectorExpanded(false);
    setStyle(value.id)
    operationsMap?.setStyle(value);
  }, [operationsMap]);

  const openSideBar = useCallback(() => setSidebarOpen(true), [setSidebarOpen]);

  return (
    <Box className={classes.root}>

      {slim ? (
        <OperationsMapButton
          className={classes.topLeftButton}
          icon={<MenuIcon />}
          onClick={openSideBar}
        />
      ) : null}

      <ContainerButton
        className={slim ? classes.topLeftButtonSlim : classes.topLeftButton}
        icon={<LayersIcon />}
        title={t("operationsMap:layersTitle")}
        width="390px"
        expanded={topLeftExpanded}
        setExpanded={setTopLeftExpanded}
        slim={slim}
      >
        <LayerSettingsPanel />
      </ContainerButton>

      {
        !slim || !topLeftExpanded ? (
          <ContainerButton
            className={classes.topRightButton}
            icon={<CarIcon />}
            title={
              <Tabs value={mapTasksTabIndex} onChange={onChangeTabIndex}>
                <Tab value={0} label={t("operationsMap:tasksTabTitle")} />
                <Tab value={1} label={t("operationsMap:vehiclesTabTitle")} />
              </Tabs>
            }
            width="390px"
            right
            expanded={topRightExpanded}
            setExpanded={setTopRightExpanded}
            slim={slim}
          >
            {
              mapTasksTabIndex === 0 ? (
                <ActiveTasksPanel />
              ) : (
                <VehiclesPanel />
              )
            }
          </ContainerButton>
        ) : null
      }

      {!slim ? (
        <OperationsMapTextButton
          className={`${classes.createMissionButton} ${topRightExpanded ? classes.createMissionButtonTopRightExpanded : ""}`}
          icon={<AddIcon />}
          onClick={createMissionDraft}
        >
          {t("operationsMap:createMission")}
        </OperationsMapTextButton>
      ) : (
        <OperationsMapButton
          className={`${classes.createMissionButtonSlim}`}
          icon={<AddIcon />}
          onClick={createMissionDraft}
        />
      )}


      <NavigationButtonWidget
        className={`${slim ? classes.navigationWidgetSlim : classes.navigationWidget} ${topLeftExpanded && !slim ? classes.navigationWidgetTopLeftExpanded : ""}`}
        map={operationsMap}
        slim={slim}
      />

      <StyleSelector
        className={`${classes.styleSelector} ${topLeftExpanded && !slim ? classes.styleSelectorTopLeftExpanded : ""}`}
        expanded={styleSelectorExpanded}
        setExpanded={setStyleSelectorExpanded}
        onChange={onStyleSelected}
        value={style}
        slim={slim}
      />

      <div className={classes.map} ref={containerRef}></div>

      <TaskDialog />

      <VehicleDialog />

      {!!popupDialog ? (
        <PopupDialog
          feature={popupDialog}
          onClose={() => setPopupDialog(null)}
        />
      ) : null}


    </Box>
  );
}

